import type { Constant } from '@pbt/pbt-ui-components'
import { Utils } from '@pbt/pbt-ui-components'

import {
  DeclineReasonToOrderDisplayStatus,
  OrderDisplayStatus,
} from '~/constants/orderStatusChip'
import { PrescriptionStates, PrescriptionType } from '~/constants/prescription'
import type { PrescriptionDeclineReasonType } from '~/constants/PrescriptionWorkflow'
import type { Prescription } from '~/types'
import {
  getPrescriptionType,
  getPrescriptionWorkflowType,
} from '~/utils/prescription'

export interface OrderStatusInput {
  prescription: Prescription
  prescriptionStates?: Constant[]
  retailOrderStates?: Constant[]
}

const CHEWY_ORDER_STATE_TO_DISPLAY_STATUS: Record<string, OrderDisplayStatus> =
  {
    Approved: OrderDisplayStatus.APPROVED,
    Shipped: OrderDisplayStatus.SHIPPED,
    Delivered: OrderDisplayStatus.DELIVERED,
    'Pending pharmacist review': OrderDisplayStatus.PENDING_PHARMACIST_REVIEW,
    'Pharmacist declined': OrderDisplayStatus.PHARMACIST_DECLINED,
  }

const getDeclinedOrderDisplayStatus = (
  rejectionReason?: string | null,
): OrderDisplayStatus | null => {
  if (!rejectionReason) {
    return OrderDisplayStatus.DECLINED_OTHER
  }

  const rejectionReasonLower = rejectionReason.toLowerCase()
  const matchedEntry = Object.entries(DeclineReasonToOrderDisplayStatus).find(
    ([reasonType]) => {
      const reasonTypeValue = reasonType as PrescriptionDeclineReasonType
      const reasonTypeString = reasonTypeValue.toLowerCase()
      return rejectionReasonLower.includes(reasonTypeString)
    },
  )

  return matchedEntry ? matchedEntry[1] : OrderDisplayStatus.DECLINED_OTHER
}

const getChewyOrderDisplayStatus = (
  prescription: Prescription,
  stateName: string,
  retailOrderStates: Constant[] | undefined,
): OrderDisplayStatus | null => {
  if (stateName === PrescriptionStates.CANCELED) {
    return OrderDisplayStatus.CANCELED
  }

  if (stateName === PrescriptionStates.DECLINED) {
    const declinedStatus = getDeclinedOrderDisplayStatus(
      prescription.rejectionReason,
    )
    if (declinedStatus) return declinedStatus
  }

  if (prescription.lineItem?.orderStateId && retailOrderStates) {
    const orderStateName = Utils.getConstantName(
      prescription.lineItem.orderStateId,
      retailOrderStates,
    )
    const orderStatus = CHEWY_ORDER_STATE_TO_DISPLAY_STATUS[orderStateName]
    if (orderStatus) return orderStatus
  }

  if (stateName === PrescriptionStates.APPROVED) {
    return OrderDisplayStatus.APPROVED
  }

  return null
}

/** In-house prescriptions show FILLED only for these workflow states. */
const IN_HOUSE_FILLED_STATES: string[] = [
  PrescriptionStates.APPROVED,
  PrescriptionStates.PRESCRIBED,
  PrescriptionStates.ORDERED,
  PrescriptionStates.SUBMITTED,
]

const getInHouseOrderDisplayStatus = (
  prescription: Prescription,
  stateName: string,
): OrderDisplayStatus | null => {
  if (stateName === PrescriptionStates.CANCELED) {
    return OrderDisplayStatus.CANCELED
  }

  if (stateName === PrescriptionStates.DECLINED) {
    const declinedStatus = getDeclinedOrderDisplayStatus(
      prescription.rejectionReason,
    )
    return declinedStatus ?? OrderDisplayStatus.DECLINED_OTHER
  }

  if (IN_HOUSE_FILLED_STATES.includes(stateName)) {
    return OrderDisplayStatus.FILLED
  }

  return null
}

export const getOrderDisplayStatus = (
  input: OrderStatusInput,
): OrderDisplayStatus | null => {
  const { prescription, prescriptionStates, retailOrderStates } = input

  const { isInHouse, isScriptOut } = getPrescriptionType(
    prescription.prescriptionType,
  )
  if (
    isScriptOut ||
    prescription.prescriptionType === PrescriptionType.SCRIPT_OUT
  ) {
    return null
  }

  const stateName = prescription.stateId
    ? Utils.getConstantName(prescription.stateId, prescriptionStates ?? [])
    : null
  if (!stateName) return null

  const { isChewyActiveRx, isChewyReactiveRx } = getPrescriptionWorkflowType({
    orderType: prescription.type,
    prescriptionType: prescription.prescriptionType,
    origin: prescription.origin,
  })
  const isChewy = isChewyActiveRx || isChewyReactiveRx

  if (isChewy) {
    return getChewyOrderDisplayStatus(
      prescription,
      stateName,
      retailOrderStates,
    )
  }

  if (isInHouse) {
    return getInHouseOrderDisplayStatus(prescription, stateName)
  }

  return null
}
